import React, { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '../../../foundation/altzero_base/base/components/ui/card';
import { Input } from '../../../foundation/altzero_base/base/components/ui/input';
import { Label } from '../../../foundation/altzero_base/base/components/ui/label';
import { Button } from '../../../foundation/altzero_base/base/components/ui/button';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import { TrendingUp, BarChart3, CreditCard, Leaf, Plus, Trash2, Loader2 } from 'lucide-react';
import { MultiYearRates, EnvironmentalCharges, NonEnergyCharges } from '../types/offerTypes';
import { Retailer } from '../services/retailerService';
import { normalizeNonEnergyCharges, formatNonEnergyLabel } from '../lib/normalizeCharges';
import { ENERGY_RATE_STEP, ENV_RATE_STEP } from '../lib/ratePrecision';
import { formatRateYearLabel, formatTermKeyLabel } from '../lib/termLabel';

// ─── Per-contract-term helpers ────────────────────────────────────────────────

const getTermsForVariant = (data: Record<string, any>, isSmooth: boolean): string[] => {
  const all = Object.keys(data);
  const suffix   = isSmooth ? '_smooth'  : '_stepped';
  const opposite = isSmooth ? '_stepped' : '_smooth';
  return all
    .filter(key => {
      if (key.endsWith(suffix))   return true;
      if (key.endsWith(opposite)) return false;
      const termData = data[key];
      if (termData?.rate_type) {
        return isSmooth ? termData.rate_type === 'Smooth' : termData.rate_type === 'Stepped';
      }
      const months = key.split('_')[0];
      return !all.some(k => k.startsWith(months + '_'));
    })
    .sort((a, b) => parseInt(a.split('_')[0], 10) - parseInt(b.split('_')[0], 10));
};

// Term heading for this table's columns. Delegates to the shared formatter so the stepped
// tables read "Year 4 (Stepped)" like everywhere else — this used to be a private
// `${months} Months` implementation, which is why the stepped view kept printing "12 Months"
// after every other screen had moved to year labels.
const formatTermLabel = (termKey: string): string => formatTermKeyLabel(termKey);

const getYearKeys = (termData: Record<string, any>): string[] =>
  Object.keys(termData)
    .filter(k => /^year_\d+$/.test(k))
    .sort((a, b) => parseInt(a.replace('year_', ''), 10) - parseInt(b.replace('year_', ''), 10));

// ─── Static constants (outside components so they never re-create) ────────────

const PER_TERM_RATE_TYPES = [
  { key: 'peak' as const,    label: 'Peak Rate',     color: '#EF4444' },
  { key: 'offpeak' as const, label: 'Off-Peak Rate', color: '#10B981' },
  { key: 'shoulder' as const,label: 'Shoulder Rate', color: '#F59E0B' },
];

const ENV_SCHEME_COLORS = [
  { text: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200',  focus: 'focus:border-green-400'  },
  { text: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   focus: 'focus:border-blue-400'   },
  { text: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', focus: 'focus:border-purple-400' },
  { text: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', focus: 'focus:border-orange-400' },
  { text: 'text-teal-700',   bg: 'bg-teal-50',   border: 'border-teal-200',   focus: 'focus:border-teal-400'   },
  { text: 'text-rose-700',   bg: 'bg-rose-50',   border: 'border-rose-200',   focus: 'focus:border-rose-400'   },
  { text: 'text-gray-700',   bg: 'bg-gray-100',  border: 'border-gray-300',   focus: 'focus:border-gray-400'   },
];

const ENV_SCHEME_COLOR_INDEX: Record<string, number> = {
  'LREC': 0, 'SREC': 1, 'VEEC/ESS/REES': 2, 'ESC': 3, 'PRC': 4, 'REPS': 5,
};

// ─── StableNumberInput ────────────────────────────────────────────────────────

interface StableNumberInputProps {
  value: number | string;
  onChange: (value: number) => void;
  placeholder?: string;
  className?: string;
  step?: string | number;
  min?: string | number;
  max?: string | number;
}

const StableNumberInput: React.FC<StableNumberInputProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  step,
  min,
  max,
}) => {
  const [localValue, setLocalValue] = useState<string>(String(value || ''));
  const isFocusedRef = useRef(false);
  const pendingRef = useRef<number | null>(null);
  const onChangeRef = useRef(onChange);
  const localRef = useRef(String(value || ''));
  const lastExternalRef = useRef(String(value || ''));

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  // Sync from parent only when not focused
  useEffect(() => {
    const next = String(value || '');
    if (next !== lastExternalRef.current && !isFocusedRef.current) {
      lastExternalRef.current = next;
      setLocalValue(next);
      localRef.current = next;
    }
  }, [value]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    setLocalValue(v);
    localRef.current = v;
    if (v === '' || /^\d*\.?\d*$/.test(v)) {
      pendingRef.current = v === '' ? 0 : parseFloat(v) || 0;
    }
  }, []);

  const handleFocus = useCallback(() => {
    isFocusedRef.current = true;
    pendingRef.current = null;
  }, []);

  const handleBlur = useCallback(() => {
    isFocusedRef.current = false;
    if (pendingRef.current !== null) {
      const final = pendingRef.current;
      lastExternalRef.current = String(final);
      onChangeRef.current(final);
      pendingRef.current = null;
    } else {
      const numVal = parseFloat(localRef.current) || 0;
      lastExternalRef.current = String(numVal);
      onChangeRef.current(numVal);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (pendingRef.current !== null) {
        onChangeRef.current(pendingRef.current);
      } else if (isFocusedRef.current) {
        const numVal = parseFloat(localRef.current) || 0;
        if (String(numVal) !== lastExternalRef.current) {
          onChangeRef.current(numVal);
        }
      }
    };
  }, []);

  return (
    <Input
      type="number"
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      placeholder={placeholder}
      className={className}
      step={step}
      min={min}
      max={max}
    />
  );
};

// ─── RateSection (MUST be outside DualRateTable to avoid remount on re-render) ─

type RateTypeEntry = { key: 'peak' | 'offpeak' | 'shoulder'; label: string; color: string; rates?: MultiYearRates };

interface RateSectionProps {
  rateTypes: RateTypeEntry[];
  isSmooth: boolean;
  environmentalCharges: EnvironmentalCharges;
  nonEnergyCharges: NonEnergyCharges;
  activeTermTab: string;
  setActiveTermTab: (t: string) => void;
  contractTermsData?: Record<string, any>;
  onContractTermRateChange?: (termKey: string, yearKey: string, rateType: 'peak' | 'offpeak' | 'shoulder', value: number) => void;
  contractYears: number;
  yearLabels: string[];
  unit: string;
  onRateChange: (rateType: 'peak' | 'offpeak' | 'shoulder', year: string, value: number, isSmooth: boolean) => void;
  onEnvironmentalChargeChange: (chargeType: string, year: string, field: 'pricePerCertificate' | 'powerPercentage' | 'ratePerKwh', value: number, isSmooth: boolean) => void;
  onNonEnergyChargeChange: (chargeName: string, value: number, isSmooth: boolean) => void;
  onAddCustomCharge: (chargeName: string, isSmooth: boolean) => void;
  onRemoveCustomCharge: (chargeName: string, isSmooth: boolean) => void;
}

const RateSection: React.FC<RateSectionProps> = ({
  rateTypes,
  isSmooth,
  environmentalCharges,
  nonEnergyCharges,
  activeTermTab,
  setActiveTermTab,
  contractTermsData,
  onContractTermRateChange,
  contractYears,
  yearLabels,
  unit,
  onRateChange,
  onEnvironmentalChargeChange,
  onNonEnergyChargeChange,
  onAddCustomCharge,
  onRemoveCustomCharge,
}) => {
  const [newChargeName, setNewChargeName] = useState('');
  const termsForVariant = contractTermsData ? getTermsForVariant(contractTermsData, isSmooth) : [];

  return (
    <div className="space-y-6">

      {/* Energy Rates Table */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700">Energy Rates ({unit})</h4>
          <span className="text-xs text-red-500 font-normal">* Peak &amp; Off-Peak rates (Year 1) are required for calculation</span>
        </div>

        {contractTermsData && termsForVariant.length > 0 ? (
          <Tabs value={activeTermTab || termsForVariant[0]} onValueChange={setActiveTermTab}>
            <TabsList className="mb-4 flex-wrap h-auto">
              {termsForVariant.map(termKey => (
                <TabsTrigger key={termKey} value={termKey} className="text-xs">
                  {formatTermLabel(termKey)}
                </TabsTrigger>
              ))}
            </TabsList>

            {termsForVariant.map(termKey => {
              const termData: Record<string, any> = contractTermsData[termKey] || {};
              const yearKeys = getYearKeys(termData);
              const colCount = Math.max(1, yearKeys.length);

              return (
                <TabsContent key={termKey} value={termKey}>
                  <div className="overflow-x-auto">
                    <div
                      className="grid gap-2 mb-3"
                      style={{ gridTemplateColumns: `120px repeat(${colCount}, 1fr)` }}
                    >
                      <div className="text-xs font-semibold text-gray-800 bg-gray-50 p-2 rounded">Rate Type</div>
                      {yearKeys.map((yk, idx) => (
                        <div key={yk} className="text-xs font-semibold text-gray-800 bg-gray-50 p-2 rounded text-center">
                          {formatRateYearLabel(idx + 1)}{idx === 0 && <span className="text-red-500 ml-0.5">*</span>}
                        </div>
                      ))}
                    </div>

                    {PER_TERM_RATE_TYPES.map(({ key, label, color }) => (
                      <div
                        key={key}
                        className="grid gap-2 items-center mb-3 p-2 hover:bg-gray-50 rounded transition-colors"
                        style={{ gridTemplateColumns: `120px repeat(${colCount}, 1fr)` }}
                      >
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                          <Label className="text-sm font-medium">{label}</Label>
                        </div>
                        {yearKeys.map(yk => (
                          <div key={yk} className="relative">
                            <StableNumberInput
                              step={ENERGY_RATE_STEP}
                              min="0"
                              placeholder={unit}
                              value={termData[yk]?.[key] ?? ''}
                              onChange={(val) => onContractTermRateChange?.(termKey, yk, key, val)}
                              className="text-center"
                            />
                            <div className="absolute -bottom-5 left-0 right-0 text-xs text-gray-400 text-center">{unit}</div>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          <div className="overflow-x-auto">
            <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `120px repeat(${contractYears}, 1fr)` }}>
              <div className="text-xs font-semibold text-gray-800 bg-gray-50 p-2 rounded">Rate Type</div>
              {yearLabels.map((label, index) => (
                <div key={index} className="text-xs font-semibold text-gray-800 bg-gray-50 p-2 rounded text-center">
                  {label}{index === 0 && <span className="text-red-500 ml-0.5">*</span>}
                </div>
              ))}
            </div>

            {rateTypes.map(({ key, label, color }) => (
              <div
                key={key}
                className="grid gap-2 items-center mb-3 p-2 hover:bg-gray-50 rounded transition-colors"
                style={{ gridTemplateColumns: `120px repeat(${contractYears}, 1fr)` }}
              >
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  <Label className="text-sm font-medium">{label}</Label>
                </div>
                {Array.from({ length: contractYears }, (_, i) => {
                  const yearKey = `year_${i + 1}` as keyof MultiYearRates;
                  const rateMap = rateTypes.find(r => r.key === key);
                  return (
                    <div key={yearKey} className="relative">
                      <StableNumberInput
                        step={ENERGY_RATE_STEP}
                        min="0"
                        placeholder={`${unit}`}
                        value={(rateMap as any)?.rates?.[yearKey] ?? ''}
                        onChange={(val) => onRateChange(key, yearKey, val, isSmooth)}
                        className="text-center"
                      />
                      <div className="absolute -bottom-5 left-0 right-0 text-xs text-gray-400 text-center">{unit}</div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Environmental Charges Section */}
      <div className="bg-white rounded-lg p-4 mb-4 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <Leaf className="w-5 h-5 text-green-600" />
          <h3 className="text-lg font-semibold text-gray-900">Environmental Charges</h3>
          <Badge variant="outline" className="text-xs">Power % &amp; $/kWh</Badge>
        </div>

        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-gray-200">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                <th className="border border-gray-200 px-3 py-3 text-left font-semibold text-gray-800 bg-gray-50">Year</th>
                {Object.keys(environmentalCharges).flatMap((scheme, si) => {
                  const c = ENV_SCHEME_COLORS[ENV_SCHEME_COLOR_INDEX[scheme] ?? (si % ENV_SCHEME_COLORS.length)];
                  return [
                    <th key={`${scheme}-cert`} className={`border border-gray-200 px-2 py-3 text-center font-semibold ${c.text} ${c.bg} text-xs`}>{scheme} – Price Per Certificate</th>,
                    <th key={`${scheme}-pct`}  className={`border border-gray-200 px-2 py-3 text-center font-semibold ${c.text} ${c.bg} text-xs`}>{scheme} – Power Percentage</th>,
                    <th key={`${scheme}-rate`} className={`border border-gray-200 px-2 py-3 text-center font-semibold ${c.text} ${c.bg} text-xs`}>{scheme} – Rate Per kWh ($/kWh)</th>,
                  ];
                })}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: contractYears }, (_, i) => {
                const year = `year_${i + 1}`;
                return (
                  <tr key={`env-${year}-${isSmooth ? 'smooth' : 'stepped'}`} className="hover:bg-gradient-to-r hover:from-gray-50 hover:to-gray-100 transition-colors">
                    <td className="border border-gray-200 px-3 py-3 font-semibold text-gray-800 bg-gray-50">{formatRateYearLabel(i + 1)}</td>
                    {Object.keys(environmentalCharges).flatMap((scheme, si) => {
                      const c = ENV_SCHEME_COLORS[ENV_SCHEME_COLOR_INDEX[scheme] ?? (si % ENV_SCHEME_COLORS.length)];
                      const schemeData = (environmentalCharges as Record<string, any>)[scheme];
                      return [
                        <td key={`${scheme}-cert`} className="border border-gray-200 px-2 py-2">
                          <StableNumberInput step="0.01" min="0" placeholder="–"
                            value={schemeData?.[year]?.pricePerCertificate || ''}
                            onChange={(val) => onEnvironmentalChargeChange(scheme, year, 'pricePerCertificate', val, isSmooth)}
                            className={`text-center text-xs ${c.border} ${c.focus}`} />
                        </td>,
                        <td key={`${scheme}-pct`} className="border border-gray-200 px-2 py-2">
                          <StableNumberInput step="0.01" min="0" max="100" placeholder="%"
                            value={schemeData?.[year]?.powerPercentage || ''}
                            onChange={(val) => onEnvironmentalChargeChange(scheme, year, 'powerPercentage', val, isSmooth)}
                            className={`text-center text-xs ${c.border} ${c.focus}`} />
                        </td>,
                        <td key={`${scheme}-rate`} className="border border-gray-200 px-2 py-2">
                          <StableNumberInput step={ENV_RATE_STEP} min="0" placeholder="0.000000000"
                            value={schemeData?.[year]?.ratePerKwh || ''}
                            onChange={(val) => onEnvironmentalChargeChange(scheme, year, 'ratePerKwh', val, isSmooth)}
                            className={`text-center text-xs ${c.border} ${c.focus}`} />
                        </td>,
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Non-Energy Charges Section */}
      <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Non-Energy Charges</h3>
          <Badge variant="outline" className="text-xs">Common Values</Badge>
        </div>

        {(() => {
          const normalized = normalizeNonEnergyCharges(nonEnergyCharges);
          const predefinedKeys = ['Meter Agreement', 'Supplementary Metering'];
          const predefinedCharges = [
            { key: 'Meter Agreement',        label: 'Meter Agreement',        color: '#3B82F6' },
            { key: 'Supplementary Metering', label: 'Supplementary Metering', color: '#10B981' },
          ];
          const customKeys = Object.keys(normalized).filter(k => !predefinedKeys.includes(k));

          return (
            <div className="grid grid-cols-1 gap-4">
              {predefinedCharges.map(({ key, label, color }) => (
                <div key={key} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: color }} />
                  <Label className="text-sm font-medium flex-1">{label}</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      step="0.01" min="0" placeholder="0.00"
                      value={normalized[key] || ''}
                      onChange={(val) => onNonEnergyChargeChange(key, val, isSmooth)}
                      className="w-24 text-center"
                    />
                    <span className="text-xs text-gray-500">$/year</span>
                  </div>
                </div>
              ))}

              {customKeys.map((chargeName) => (
                <div key={chargeName} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className="w-3 h-3 rounded-full bg-purple-500" />
                  <Label className="text-sm font-medium flex-1">{formatNonEnergyLabel(chargeName)}</Label>
                  <div className="flex items-center gap-2">
                    <StableNumberInput
                      step="0.01" min="0" placeholder="0.00"
                      value={normalized[chargeName] || ''}
                      onChange={(val) => onNonEnergyChargeChange(chargeName, val, isSmooth)}
                      className="w-24 text-center"
                    />
                    <span className="text-xs text-gray-500">$/year</span>
                    <button
                      onClick={() => onRemoveCustomCharge(chargeName, isSmooth)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {/* Add Custom Charge */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Plus className="w-4 h-4 text-gray-500" />
            <Label className="text-sm font-medium text-gray-700">Add Custom Charge</Label>
          </div>
          <div className="flex gap-2">
            <Input
              placeholder="Enter charge name (e.g., 'Connection Fee')"
              value={newChargeName}
              onChange={(e) => setNewChargeName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newChargeName.trim() && !nonEnergyCharges[newChargeName.trim()]) {
                  onAddCustomCharge(newChargeName.trim(), isSmooth);
                  setNewChargeName('');
                }
              }}
              className="flex-1"
            />
            <button
              onClick={() => {
                if (newChargeName.trim() && !nonEnergyCharges[newChargeName.trim()]) {
                  onAddCustomCharge(newChargeName.trim(), isSmooth);
                  setNewChargeName('');
                }
              }}
              disabled={!newChargeName.trim() || !!nonEnergyCharges[newChargeName.trim()]}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── DualRateTable ────────────────────────────────────────────────────────────

interface DualRateTableProps {
  contractYears: number;
  smoothPeakRates: MultiYearRates;
  smoothOffpeakRates: MultiYearRates;
  smoothShoulderRates: MultiYearRates;
  smoothEnvironmentalCharges: EnvironmentalCharges;
  smoothNonEnergyCharges: NonEnergyCharges;
  steppedPeakRates: MultiYearRates;
  steppedOffpeakRates: MultiYearRates;
  steppedShoulderRates: MultiYearRates;
  steppedEnvironmentalCharges: EnvironmentalCharges;
  steppedNonEnergyCharges: NonEnergyCharges;
  onRateChange: (rateType: 'peak' | 'offpeak' | 'shoulder', year: string, value: number, isSmooth: boolean) => void;
  onEnvironmentalChargeChange: (chargeType: string, year: string, field: 'pricePerCertificate' | 'powerPercentage' | 'ratePerKwh', value: number, isSmooth: boolean) => void;
  onNonEnergyChargeChange: (chargeName: string, value: number, isSmooth: boolean) => void;
  onAddCustomCharge: (chargeName: string, isSmooth: boolean) => void;
  onRemoveCustomCharge: (chargeName: string, isSmooth: boolean) => void;
  retailer?: Retailer;
  title?: string;
  unit?: string;
  contractTermsData?: Record<string, any>;
  onContractTermRateChange?: (termKey: string, yearKey: string, rateType: 'peak' | 'offpeak' | 'shoulder', value: number) => void;
  selectedTermMonths?: number;
}

const DualRateTable: React.FC<DualRateTableProps> = ({
  contractYears,
  smoothPeakRates,
  smoothOffpeakRates,
  smoothShoulderRates,
  smoothEnvironmentalCharges,
  smoothNonEnergyCharges,
  steppedPeakRates,
  steppedOffpeakRates,
  steppedShoulderRates,
  steppedEnvironmentalCharges,
  steppedNonEnergyCharges,
  onRateChange,
  onEnvironmentalChargeChange,
  onNonEnergyChargeChange,
  onAddCustomCharge,
  onRemoveCustomCharge,
  retailer,
  title = "Multi-Year Rates & Charges",
  unit = "c/kWh",
  contractTermsData,
  onContractTermRateChange,
  selectedTermMonths,
}) => {
  const [activeTab, setActiveTab] = useState('smooth');
  const [isTabLoading, setIsTabLoading] = useState(false);
  const [activeSmoothTermTab, setActiveSmoothTermTab] = useState<string>('');
  const [activeSteppedTermTab, setActiveSteppedTermTab] = useState<string>('');

  const yearLabels = useMemo(
    () => Array.from({ length: contractYears }, (_, i) => formatRateYearLabel(i + 1)),
    [contractYears]
  );

  // Build rate type arrays with actual rates data attached
  const smoothRateTypes = useMemo(() => [
    { key: 'peak'    as const, label: 'Peak Rate',     color: '#EF4444', rates: smoothPeakRates },
    { key: 'offpeak' as const, label: 'Off-Peak Rate', color: '#10B981', rates: smoothOffpeakRates },
    { key: 'shoulder'as const, label: 'Shoulder Rate', color: '#F59E0B', rates: smoothShoulderRates },
  ], [smoothPeakRates, smoothOffpeakRates, smoothShoulderRates]);

  const steppedRateTypes = useMemo(() => [
    { key: 'peak'    as const, label: 'Peak Rate',     color: '#EF4444', rates: steppedPeakRates },
    { key: 'offpeak' as const, label: 'Off-Peak Rate', color: '#10B981', rates: steppedOffpeakRates },
    { key: 'shoulder'as const, label: 'Shoulder Rate', color: '#F59E0B', rates: steppedShoulderRates },
  ], [steppedPeakRates, steppedOffpeakRates, steppedShoulderRates]);

  useEffect(() => {
    if (contractTermsData) {
      const smoothTerms  = getTermsForVariant(contractTermsData, true);
      const steppedTerms = getTermsForVariant(contractTermsData, false);
      if (smoothTerms.length  > 0 && !activeSmoothTermTab)  setActiveSmoothTermTab(smoothTerms[0]);
      if (steppedTerms.length > 0 && !activeSteppedTermTab) setActiveSteppedTermTab(steppedTerms[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractTermsData]);

  useEffect(() => {
    if (!contractTermsData || selectedTermMonths == null) return;
    const smoothTerms  = getTermsForVariant(contractTermsData, true);
    const steppedTerms = getTermsForVariant(contractTermsData, false);
    const ms = smoothTerms.find(k  => parseInt(k.split('_')[0], 10) === selectedTermMonths);
    const mp = steppedTerms.find(k => parseInt(k.split('_')[0], 10) === selectedTermMonths);
    if (ms) setActiveSmoothTermTab(ms);
    if (mp) setActiveSteppedTermTab(mp);
  }, [selectedTermMonths, contractTermsData]);

  const handleTabChange = (newTab: string) => {
    if (newTab !== activeTab) {
      setIsTabLoading(true);
      setTimeout(() => {
        setActiveTab(newTab);
        setIsTabLoading(false);
      }, 300);
    }
  };

  const commonSectionProps = {
    contractTermsData,
    onContractTermRateChange,
    contractYears,
    yearLabels,
    unit,
    onRateChange,
    onEnvironmentalChargeChange,
    onNonEnergyChargeChange,
    onAddCustomCharge,
    onRemoveCustomCharge,
  };

  return (
    <Card className="w-full border border-gray-200 bg-white">
      <CardHeader className="pb-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {retailer?.logo && (
              <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gray-100">
                <img
                  src={retailer.logo}
                  alt={`${retailer.name} logo`}
                  className="max-w-full max-h-6 object-contain"
                  onError={(e) => {
                    const t = e.currentTarget;
                    t.style.display = 'none';
                    const p = t.parentElement;
                    if (p) { p.innerHTML = retailer.name.charAt(0); p.style.fontWeight = 'bold'; }
                  }}
                />
              </div>
            )}
            <div>
              <h3 className="text-xl font-bold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1">Configure rates for both Smooth and Stepped pricing structures</p>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger
              value="smooth"
              disabled={isTabLoading}
              className={`flex items-center gap-2 transition-all duration-200 ${isTabLoading && activeTab !== 'smooth' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isTabLoading && activeTab !== 'smooth' ? <Loader2 className="w-4 h-4 animate-spin" /> : <TrendingUp className="w-4 h-4" />}
              Smooth Rates
            </TabsTrigger>
            <TabsTrigger
              value="stepped"
              disabled={isTabLoading}
              className={`flex items-center gap-2 transition-all duration-200 ${isTabLoading && activeTab !== 'stepped' ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isTabLoading && activeTab !== 'stepped' ? <Loader2 className="w-4 h-4 animate-spin" /> : <BarChart3 className="w-4 h-4" />}
              Stepped Rates
            </TabsTrigger>
          </TabsList>

          <TabsContent value="smooth" className="mt-6">
            {isTabLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                  <p className="text-sm text-gray-600">Loading Smooth Rates...</p>
                </div>
              </div>
            ) : (
              <RateSection
                {...commonSectionProps}
                rateTypes={smoothRateTypes}
                isSmooth={true}
                environmentalCharges={smoothEnvironmentalCharges}
                nonEnergyCharges={smoothNonEnergyCharges}
                activeTermTab={activeSmoothTermTab}
                setActiveTermTab={setActiveSmoothTermTab}
              />
            )}
          </TabsContent>

          <TabsContent value="stepped" className="mt-6">
            {isTabLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                  <p className="text-sm text-gray-600">Loading Stepped Rates...</p>
                </div>
              </div>
            ) : (
              <RateSection
                {...commonSectionProps}
                rateTypes={steppedRateTypes}
                isSmooth={false}
                environmentalCharges={steppedEnvironmentalCharges}
                nonEnergyCharges={steppedNonEnergyCharges}
                activeTermTab={activeSteppedTermTab}
                setActiveTermTab={setActiveSteppedTermTab}
              />
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default DualRateTable;
